import React, { FC, useState } from 'react';

import { config, yup } from 'data';
import { url } from 'helpers';
import { invoiceService } from 'services';
import { useForm, useLang, useMutation } from 'hooks';
import { useInvoiceBasicQuery } from 'hooks/queries';
import { InvoiceQr } from 'components/layout';
import { Form, Input, Modal, Spin } from 'components/ui';
import { ModalBaseProps } from 'types/components';
import { InvoiceCreateParams } from 'types/services';

import styles from './styles.module.css';

type FormValues = InvoiceCreateParams;

const initialValues: Omit<FormValues, 'amount'> = {
  purpose: '',
};

const validationSchema = yup.object({
  purpose: yup
    .string()
    .required()
    .min(config.INVOICE_PURPOSE_MIN_LENGTH)
    .max(config.INVOICE_PURPOSE_MAX_LENGTH),
  amount: yup
    .number()
    .required()
    .decimal()
    .min(config.INVOICE_AMOUNT_MIN)
    .max(config.INVOICE_AMOUNT_MAX)
    .transform((value) => value || 0),
});

const InvoiceModal: FC<ModalBaseProps> = ({ onCancel, ...props }) => {
  const form = useForm<FormValues>();
  const lang = useLang();

  const [invoiceDisplayId, setInvoiceDisplayId] = useState('');

  const invoiceQuery = useInvoiceBasicQuery(invoiceDisplayId);

  const createInvoiceMutation = useMutation({
    mutationFn: invoiceService.createInvoice,
    successNotification: lang.get('invoice.createModal.success'),
  });

  const handleSubmit = async (values: FormValues) => {
    const response = await createInvoiceMutation.mutateAsync(values);

    setInvoiceDisplayId(response.displayId);
  };

  const handleCancel = () => {
    setInvoiceDisplayId('');

    onCancel();
  };

  const qr = Boolean(invoiceDisplayId);
  const invoice = invoiceQuery.data;
  const invoiceLink = url.toInvoicePaymentFull(invoiceDisplayId);

  return (
    <Modal
      className={qr ? styles.root_qr : styles.root}
      title={qr ? lang.get('invoice.createModal.qrTitle') : lang.get('invoice.createModal.title')}
      caption={qr ? lang.get('invoice.createModal.qrCaption') : lang.get('invoice.createModal.caption')}
      okText={qr ? lang.get('common.actions.copy') : lang.get('common.actions.create')}
      cancelText={qr ? lang.get('common.actions.close') : null}
      okButtonProps={{ block: qr }}
      cancelButtonProps={{ block: qr }}
      confirmLoading={createInvoiceMutation.isPending || invoiceQuery.isLoading}
      onOk={qr ? () => window.navigator.clipboard.writeText(invoiceLink) : form.submit}
      onCancel={handleCancel}
      {...props}
    >
      {qr ? (
        invoice ? <InvoiceQr invoice={invoice} /> : <Spin className={styles.spin} />
      ) : (
        <Form
          form={form}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onFinish={handleSubmit}
        >
          <Form.Item name="purpose" label={lang.get('invoice.createModal.purpose.label')}>
            <Input placeholder={lang.get('invoice.createModal.purpose.placeholder')} />
          </Form.Item>
          <Form.Item name="amount" label={lang.get('invoice.createModal.amount.label')}>
            <Input
              type="number"
              placeholder={lang.get('invoice.createModal.amount.placeholder')}
              suffix={config.DEFAULT_CURRENCY}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default InvoiceModal;
